import type { i18n as I18nInstance } from 'i18next';

export type MissingKeyRecord = {
  key: string;
  ns: string;
  languages: string[];
  route: string;
  count: number;
  firstSeen: string;
};

const registry = new Map<string, MissingKeyRecord>();

/** Ruta visible en la que se ha detectado la clave, incluyendo el ancla de sección. */
function currentRoute(): string {
  if (typeof window === 'undefined') return 'ssr';
  const { pathname, hash } = window.location;
  return `${pathname}${hash || ''}`;
}

export function getMissingTranslations(): MissingKeyRecord[] {
  return [...registry.values()].sort((a, b) => b.count - a.count);
}

export function clearMissingTranslations(): void {
  registry.clear();
}

/**
 * Handler de i18next para claves ausentes o vacías.
 * Registra la clave, los idiomas afectados y la ruta, y avisa una sola vez por combinación.
 */
export function registerMissingKeyHandler(i18n: I18nInstance): void {
  i18n.options.saveMissing = true;
  // Un valor vacío se trata como ausente para que también dispare el aviso.
  i18n.options.returnEmptyString = false;

  i18n.on('missingKey', (lngs, namespace, key) => {
    const languages = Array.isArray(lngs) ? lngs : [lngs];
    const route = currentRoute();
    const id = `${languages.join(',')}|${namespace}|${key}|${route}`;
    const existing = registry.get(id);

    if (existing) {
      existing.count += 1;
      return;
    }

    registry.set(id, {
      key,
      ns: namespace,
      languages,
      route,
      count: 1,
      firstSeen: new Date().toISOString(),
    });

    if (import.meta.env.DEV) {
      console.warn(
        `[i18n] Falta la traducción "${key}" (namespace "${namespace}") en ${languages.join(', ')}.\n` +
          `        Ruta afectada: ${route}\n` +
          `        Añádela en src/i18n/locales/<idioma>.json y ejecuta: npm run check:i18n`,
      );
    }
  });

  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__i18nMissing = getMissingTranslations;
  }
}
